// Plan 30-01 — Pitfall 5 lock: single-RAF tweedle.js driver.
//
// Per 30-RESEARCH §5 Pitfall 5: tweedle.js@2.1.0 ships Group.shared as a
// purely manual update target (no auto-RAF), but a downstream consumer
// could spin a second RAF loop if it called requestAnimationFrame +
// Group.shared.update() itself. This bootstrap establishes the canonical
// driver pattern: ALL tween updates are driven by the Pixi Application
// Ticker — never by an independent RAF call. Teardown drains the shared
// group so destroyed sprites' tweens cannot hold the sprite ref alive
// past unmount.
//
// API surface (locked in plan §<interfaces>):
//   installTweedleTickerDriver(ticker): () => void
// Call ONCE per Pixi.Application lifecycle (typically at LetterLayer
// mount). The returned teardown removes the ticker callback AND drains
// Group.shared.
//
// IMPLEMENTATION NOTE — tweedle.js Group has no `autoUpdate` property in
// v2.1.0. I verified this by reading
// apps/web/node_modules/tweedle.js/dist/tweedle.es.js (Group class lines
// 53-194 — Group exposes shared / isPaused / pause / resume / now /
// getAll / removeAll / add / remove / update, none of which start any
// RAF loop). So the "disable auto-RAF" half of Pitfall 5 is a no-op for
// the current dep version — left as a defensive feature-detect in case
// of future tweedle versions or downstream patches.

import type { Ticker } from 'pixi.js'
import { Group } from 'tweedle.js'

/**
 * Per-ticker driver registry (PERF-01 / PERF-02 fix).
 *
 * The OLD implementation added a fresh `cb` to the ticker on EVERY call and
 * drained `Group.shared.removeAll()` on EVERY teardown. Both are wrong once
 * more than one live component drives the same ticker — and in a real match
 * several do simultaneously: LetterLayer (always), AnchorPlacementOverlay
 * (placement turns), QuillTargetOverlay (Quill targeting). The consequences:
 *
 *   - N callbacks each call `Group.shared.update(deltaMS)` per frame → every
 *     tween in the whole app advances at N× speed (PERF-01).
 *   - Unmounting ANY one of them called `Group.shared.removeAll()`, killing
 *     every OTHER live layer's in-flight tweens mid-animation (PERF-02).
 *
 * The fix is reference counting keyed by the Ticker instance: exactly one
 * `cb` is ever added per ticker (so tweens advance at 1× regardless of how
 * many components install), and `removeAll()` runs ONLY when the last
 * consumer detaches (so a transient overlay unmounting never nukes the
 * persistent layer's tweens, while the leaked-target-ref guard still fires
 * once the whole Pixi Application tears down).
 *
 * Keyed by a WeakMap so distinct Pixi Applications (e.g. the DEV surface
 * routes vs the match canvas) keep independent counts and a torn-down
 * ticker's entry is GC'd automatically.
 */
interface DriverEntry {
  count: number
  readonly cb: () => void
}
const DRIVERS = new WeakMap<object, DriverEntry>()

/**
 * Disables tweedle's auto-RAF (no-op in current tweedle@2.1.0 — see
 * implementation note above) and binds `Group.shared.update` to the
 * provided Pixi Ticker so all tweens advance in lockstep with the
 * renderer's frame cadence.
 *
 * Idempotent per ticker: calling it twice (or from four live components)
 * registers the update callback exactly ONCE and ref-counts the installs.
 *
 * Returns a teardown function that:
 *   1. Decrements this ticker's install count.
 *   2. When the count reaches zero (the LAST consumer detached): removes the
 *      ticker callback and calls `Group.shared.removeAll()` to evict any
 *      lingering tweens that still hold references to now-destroyed sprite
 *      targets (Pitfall 5 — "destroyed sprites leak target refs").
 *
 * Each returned teardown is itself idempotent: calling it a second time is a
 * no-op and does not double-decrement.
 */
export function installTweedleTickerDriver(ticker: Ticker): () => void {
  // 1. Defensive: if a future tweedle exposes an autoUpdate switch, flip
  // it off. Today's tweedle.js@2.1.0 has no such property — this branch
  // is a forward-compat hatch and a no-op against the current dep.
  const sharedAny = Group.shared as unknown as { autoUpdate?: boolean }
  if (typeof sharedAny.autoUpdate !== 'undefined') {
    sharedAny.autoUpdate = false
  }

  // 2. Add the single update callback for this ticker on first install only.
  // Use ticker.deltaMS (the *measured* frame delta, accounting for
  // Ticker.speed and minFPS clamps) so tweens slow/pause when the renderer
  // slows/pauses.
  const key = ticker as unknown as object
  let entry = DRIVERS.get(key)
  if (!entry) {
    const cb = (): void => {
      Group.shared.update(ticker.deltaMS)
    }
    ticker.add(cb)
    entry = { count: 0, cb }
    DRIVERS.set(key, entry)
  }
  entry.count += 1

  // 3. Teardown — idempotent per returned closure; ref-counted per ticker.
  let torndown = false
  return (): void => {
    if (torndown) return
    torndown = true
    const e = DRIVERS.get(key)
    if (!e) return
    e.count -= 1
    if (e.count <= 0) {
      ticker.remove(e.cb)
      DRIVERS.delete(key)
      Group.shared.removeAll()
    }
  }
}
